import { toNumber } from 'lodash';
import { ProjectSearchParams, ProjectData } from '../../../lib/propertyTypes';
import { latestActiveProjectsParams } from '../constants';
import { projectNetTypeMapping } from '../domain/propertyTypes';
import { getDDLocationCode } from './getDDLocationCode';
import { extractPropertyTypeFromProjectData } from './extractPropertyType';
import { getDDLocationDetails } from './getDDLocationDetails';
import { PostSubcategory } from '../domain/postCategories';
import { getPostCategoryLabel } from './getPostCategoryLabel';
const getLocationGroups = (locationSearchType: string): string[] => {
  switch (locationSearchType) {
    case 'district':
      return ['D', 'L'];
    case 'zone':
      return ['A'];
    default:
      return [];
  }
};

export const prepareParamsForSearchProjects = (
  searchQuery: {
    location?: string;
    propertyType?: string;
    freeText?: string;
    minPrice?: string;
    maxPrice?: string;
  },
  searchType?: string,
): ProjectSearchParams => {
  const { propertyType, location, freeText, minPrice, maxPrice } = searchQuery;
  const params: ProjectSearchParams = {};

  const propertyTypeCodes =
    propertyType && projectNetTypeMapping[propertyType]?.join(',');

  if (propertyTypeCodes) {
    params['property_types'] = propertyTypeCodes;
  }

  if (freeText) {
    params['name'] = freeText;
  }

  if (minPrice && !isNaN(toNumber(minPrice))) {
    params['min_price'] = toNumber(minPrice);
  }

  if (maxPrice && !isNaN(toNumber(maxPrice))) {
    params['max_price'] = toNumber(maxPrice);
  }

  if (searchType === 'district' || searchType === 'zone') {
    const locationGroups = getLocationGroups(searchType);
    const { code: locationCode, type: locationType } = getDDLocationCode(
      location,
      locationGroups,
    );
    // both province and district can be used for district-based search
    // but they use different parameter names (level_1 and level_2)
    if (locationType === 'province' && locationCode) {
      params['administrative_level_1'] = locationCode;
    }
    if (locationType === 'district' && locationCode) {
      params['administrative_level_2'] = locationCode;
    }
    if (locationType === 'area' && locationCode) {
      params['administrative_level_3'] = locationCode;
    }

    if (!locationCode && !propertyTypeCodes && location) {
      params['name'] = location;
    }
  } else if (searchType === 'road') {
    params['street_name'] = location;
  }
  return {
    ...params,
    ...latestActiveProjectsParams,
  };
};

export const prepareParamsForSearchProjectsSuggestions = (
  freeText?: string,
) => {
  const params: ProjectSearchParams = {};
  if (freeText) {
    params['name'] = freeText;
    params['limit'] = 100;
  }
  return {
    ...params,
    ...latestActiveProjectsParams,
  };
};

export const prepareParamsForSeoMetaComposerRequest = (
  data: ProjectData,
  category?: PostSubcategory,
) => {
  const {
    name,
    addresses,
    developer,
    maintenance_fee,
    starting_price,
    total_units,
    project_status,
    streetname,
    tenure,
    district_code,
    max_price,
    priceTypes,
  } = data;

  const administrativeLevels = addresses?.[0]?.administrative_levels || {};
  const { level1, level3 } = administrativeLevels;
  const formattedStartingPrice = starting_price?.value
    ? (starting_price?.value / 1000000).toString()
    : '';
  const formattedMaxPrice = max_price?.value
    ? (max_price?.value / 1000000).toString()
    : '';

  return {
    projectName: name,
    propertyType: extractPropertyTypeFromProjectData(data),
    priceFrom: formattedStartingPrice,
    priceTo: formattedMaxPrice,
    developerName: developer?.name,
    projectStatus: project_status?.name,
    locationProvince: getDDLocationDetails(level1?.code)?.thai_name,
    locationDistrict: getDDLocationDetails(district_code)?.thai_name,
    locationArea: getDDLocationDetails(level3?.code)?.thai_name,
    locationStreetName: streetname,
    tenureType: tenure?.name,
    totalUnits: total_units?.toString(),
    postCategory: getPostCategoryLabel(category)?.thai_name,
    segment: '',
    maintenanceFee: maintenance_fee?.value
      ? `${maintenance_fee.value} บาท/ตร.ม./เดือน`
      : '',
    pricePerSquareMeter: priceTypes?.per_square_meter,
    furnishedStatus: '',
  };
};
